import React, { useState } from 'react'

import DOMPurify from 'dompurify'

import validation from '../../../validation'
import AddButton from '../../controls/AddButton'
import DatePicker from '../../controls/DatePicker'
import RemoveButton from '../../controls/RemoveButton'
import TextArea from '../../controls/TextArea'

export default {
  name: 'Diagnostic Tests',
  group: 'Case Details',
  validationSchema: getValidationSchema(),
  Component,
}

function getValidationSchema() {
  return validation.object({
    tests: validation
      .arrayOf(
        validation.object({
          type: validation.shortText('Type of Test').required(),
          date: validation.shortText('Date of Test').nullable(),
          results: validation.richText('Results').nullable(),
        }),
      )
      .nullable(),
  })
}

function Component({ addItemToArray, removeItemFromArray, createInputProps, getValue, ErrorMessage }) {
  const [editingIndex, setEditingIndex] = useState(null)
  const tests = getValue('tests', [])

  const handleAddTest = () => {
    const nextIndex = tests.length
    addItemToArray('tests', {})
    setEditingIndex(nextIndex)
  }

  const handleRemove = index => {
    removeItemFromArray('tests', index)
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  return (
    <div>
      <h4 className="pb-3">Diagnostic Tests</h4>
      {tests.map((x, index) => {
        const namePrefix = `tests[${index}]`
        const isEditing = editingIndex === index
        return (
          <React.Fragment key={namePrefix}>
            <Test
              namePrefix={namePrefix}
              createInputProps={createInputProps}
              getValue={getValue}
              ErrorMessage={ErrorMessage}
              isEditing={isEditing}
              onEdit={() => setEditingIndex(index)}
              onDone={() => setEditingIndex(null)}
              handleRemove={() => handleRemove(index)}
            />
            <hr />
          </React.Fragment>
        )
      })}
      <AddButton id="addTestButton" onClick={handleAddTest}>
        Add Diagnostic Test
      </AddButton>
    </div>
  )
}

function Test({ namePrefix, createInputProps, ErrorMessage, handleRemove, getValue, isEditing, onEdit, onDone }) {
  const type = getValue(`${namePrefix}.type`, '')
  const date = getValue(`${namePrefix}.date`, '')
  const results = getValue(`${namePrefix}.results`, '')
  const hasResults = Boolean(results && results.replace(/<[^>]*>/g, '').trim())

  if (!isEditing) {
    return (
      <div className="d-flex align-items-start justify-content-between clearfix">
        <div className="w-100 mr-3">
          <div>
            <span className="font-weight-semibold">{type || <em className="text-muted">Untitled Test</em>}</span>
            {date && (
              <span className="text-muted ml-3" style={{ fontSize: '0.9em' }}>
                {date}
              </span>
            )}
          </div>
          {hasResults && (
            <div className="text-muted mt-1" style={{ fontSize: '0.85em' }}>
              <span>Results: </span>
              <div className="d-inline" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(results) }} />
            </div>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onEdit} title="Edit this diagnostic test">
            <i className="fas fa-pen" aria-hidden="true" />
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    )
  }

  return (
    <div className="clearfix">
      <div className="form-row">
        <div className="form-group col-md-8">
          <label htmlFor={`${namePrefix}.type`} className="form-label">
            Type of Test
          </label>
          <input
            type="text"
            id={`${namePrefix}.type`}
            maxLength={validation.maxLengths.name}
            className="form-control"
            {...createInputProps(`${namePrefix}.type`)}
          />
          <ErrorMessage name={`${namePrefix}.type`} />
        </div>
        <div className="form-group col-md-4">
          <label htmlFor={`${namePrefix}.date`} className="form-label">
            Date of Test
          </label>
          <DatePicker {...createInputProps(`${namePrefix}.date`)} />
          <ErrorMessage name={`${namePrefix}.date`} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group col-md-12 mb-0">
          <label htmlFor={`${namePrefix}.results`} className="form-label">
            Results
          </label>
          <TextArea
            areaProps={{ ...createInputProps(`${namePrefix}.results`) }}
            rows={3}
            maxLength={validation.maxLengths.richText}
          />
          <ErrorMessage name={`${namePrefix}.results`} />
        </div>
      </div>
      <div className="form-row mb-2 mt-2">
        <div className="col-12 d-flex align-items-end justify-content-end pb-1" style={{ gap: '8px' }}>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onDone}>
            Done
          </button>
          <RemoveButton id={`remove-${namePrefix}`} className="btn-sm" onClick={handleRemove}>
            Remove
          </RemoveButton>
        </div>
      </div>
    </div>
  )
}
